/**
 * Freeze the document, including iOS Safari's root scroller. A fixed body keeps
 * the rendered page at its current offset; overflow alone does not do that on
 * all mobile browsers. Only scrollable descendants of the drawer own gestures.
 */
export function lockDocumentScroll(drawer: HTMLElement): () => void {
  const { documentElement, body } = document;
  const { scrollX, scrollY } = window;

  function setStyles(element: HTMLElement, values: Record<string, string>) {
    const previous = Object.keys(values).map((property) => ({
      property,
      value: element.style.getPropertyValue(property),
      priority: element.style.getPropertyPriority(property),
    }));
    for (const [property, value] of Object.entries(values)) {
      element.style.setProperty(property, value, "important");
    }
    return () => {
      for (const { property, value, priority } of previous) {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      }
    };
  }

  // Keep restoration synchronous even if the page normally scrolls smoothly.
  const restoreScrollBehavior = setStyles(documentElement, {
    "scroll-behavior": "auto",
  });
  const restoreRoot = setStyles(documentElement, {
    "overflow-x": "hidden",
    "overflow-y": "hidden",
    "overscroll-behavior-x": "none",
    "overscroll-behavior-y": "none",
  });
  const restoreBody = setStyles(body, {
    position: "fixed",
    top: `${-scrollY}px`,
    left: `${-scrollX}px`,
    width: "100%",
    "overflow-x": "hidden",
    "overflow-y": "hidden",
  });

  function canScroll(target: EventTarget | null, deltaY: number): boolean {
    // Quick actions and account controls are drawer descendants. If a future
    // action opens a portaled modal, coordinate its modal/scroll ownership here
    // rather than allowing arbitrary background surfaces through this lock.
    if (!(target instanceof Element) || !drawer.contains(target)) return false;
    for (
      let element: Element | null = target;
      element && element !== drawer;
      element = element.parentElement
    ) {
      if (!(element instanceof HTMLElement)) continue;
      const overflow = window.getComputedStyle(element).overflowY;
      if (overflow !== "auto" && overflow !== "scroll") continue;
      const remaining = element.scrollHeight - element.clientHeight;
      if (
        remaining > 0 &&
        (deltaY < 0 ? element.scrollTop > 0 : element.scrollTop < remaining - 1)
      ) return true;
    }
    return false;
  }

  function wheel(event: WheelEvent) {
    if (event.ctrlKey) return; // Keep browser zoom available.
    if (!canScroll(event.target, event.deltaY)) event.preventDefault();
  }

  let touchY = 0;
  function touchStart(event: TouchEvent) {
    touchY = event.touches[0]?.clientY ?? 0;
  }
  function touchMove(event: TouchEvent) {
    // Preserve pinch zoom. A one-finger drag must belong to a drawer scroller;
    // prevent boundary gestures as a fallback for older iOS overscroll support.
    if (event.touches.length !== 1) return;
    const nextY = event.touches[0]!.clientY;
    const deltaY = touchY - nextY;
    touchY = nextY;
    if (!deltaY || !canScroll(event.target, deltaY)) event.preventDefault();
  }

  document.addEventListener("wheel", wheel, { passive: false });
  document.addEventListener("touchstart", touchStart, { passive: true });
  document.addEventListener("touchmove", touchMove, { passive: false });

  let released = false;
  return () => {
    if (released) return;
    released = true;
    document.removeEventListener("wheel", wheel);
    document.removeEventListener("touchstart", touchStart);
    document.removeEventListener("touchmove", touchMove);
    restoreBody();
    restoreRoot();
    window.scrollTo(scrollX, scrollY);
    restoreScrollBehavior();
  };
}
